import * as log from '@sb/log';
import type { GetHardwareConfigResult } from '@sb/messenger/auto_generated/messenger/arm_control_bot_service';
import type { ArmControlBotConnector } from '@sb/messenger/connectors/generated/ArmControlBotConnector';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { ModelType as ModelTypeEnum } from './ModelType';
import { ModelType, CBVoltage } from './ModelType';

const ns = log.namespace('getModelType');

type ModelUpdateListener = (
  newModelType: ModelTypeEnum,
) => void | Promise<void>;

type RevisionUpdateListener = (
  newRevision: string,
  previousRevision: string | undefined,
) => void | Promise<void>;

type CBVoltageUpdateListener = (
  newVoltage: CBVoltage | undefined,
) => void | Promise<void>;

let hardwareConfigSubscriptionActive = false;
const modelUpdateListeners = new Set<ModelUpdateListener>();
const revisionUpdateListeners = new Set<RevisionUpdateListener>();
const cbVoltageUpdateListeners = new Set<CBVoltageUpdateListener>();
let cachedHardwareConfig: GetHardwareConfigResult | undefined;

/**
 * Allows callers to clear the subscription guard so a new connector can resubscribe.
 * Useful when the underlying messenger node is recreated after a bot restart.
 */
export function resetHardwareConfigSubscription(): void {
  hardwareConfigSubscriptionActive = false;
}

const convertModelType = (
  input: string | undefined,
): ModelTypeEnum | undefined => {
  if (input === 'THOR') {
    return ModelType.enum.Thor;
  }

  if (input === 'SPARK') {
    return ModelType.enum.Spark;
  }

  if (input && input !== 'UNKNOWN') {
    return ModelType.enum.RO1;
  }

  return undefined;
};

const handleHardwareConfigUpdate = async (result: GetHardwareConfigResult) => {
  const newModelType = convertModelType(result.config?.hardwareVariant);

  if (!newModelType) {
    return;
  }

  const previousModelType = convertModelType(
    cachedHardwareConfig?.config?.hardwareVariant,
  );

  const previousRevision = cachedHardwareConfig?.config?.armRevision;

  const previousVoltage = CBVoltage.safeParse(
    cachedHardwareConfig?.cbConfig?.voltage,
  );

  cachedHardwareConfig = result;

  if (newModelType !== previousModelType) {
    for (const listener of modelUpdateListeners) {
      try {
        await listener(newModelType);
      } catch (e) {
        log.error(
          ns`handleHardwareConfigUpdate`,
          'Failed to dispatch hardware config update to listener',
          e,
        );
      }
    }
  }

  const newRevision = result.config?.armRevision;

  if (newRevision && newRevision !== previousRevision) {
    for (const listener of revisionUpdateListeners) {
      try {
        await listener(newRevision, previousRevision);
      } catch (e) {
        log.error(
          ns`handleHardwareConfigUpdate`,
          'Failed to dispatch revision update to listener',
          e,
        );
      }
    }
  }

  // Fire when the control-box voltage first resolves or changes, so boot-time
  // consumers (e.g. the speed profile) can recompute once ACB sampling resolves
  // the real voltage. See onCBVoltageUpdate.
  const newVoltage = CBVoltage.safeParse(result.cbConfig?.voltage);

  if (
    newVoltage.success &&
    (!previousVoltage.success || newVoltage.data !== previousVoltage.data)
  ) {
    for (const listener of cbVoltageUpdateListeners) {
      try {
        await listener(newVoltage.data);
      } catch (e) {
        log.error(
          ns`handleHardwareConfigUpdate`,
          'Failed to dispatch CB voltage update to listener',
          e,
        );
      }
    }
  }
};

/**
 * Ensures we are subscribed to the hardware config topic and optionally initializes the cached hardware config. Subsequent calls are no-ops.
 */
export function initializeHardwareConfigSubscription(
  acbConnector: ArmControlBotConnector,
  initialModelType?: string,
) {
  if (hardwareConfigSubscriptionActive) {
    return;
  }

  if (initialModelType) {
    cachedHardwareConfig = {
      config: {
        hardwareVariant: initialModelType.toUpperCase(), // to align with the enum values coming from ACB
        jointSerials: [],
      },
      cbConfig: cachedHardwareConfig?.cbConfig,
      errors: cachedHardwareConfig?.errors || [],
    };
  }

  try {
    acbConnector.subscribeToHardwareConfig(async (result) => {
      await handleHardwareConfigUpdate(result);
    });

    hardwareConfigSubscriptionActive = true;
  } catch (e) {
    log.error(
      ns`initializeHardwareConfigSubscription`,
      'Failed to subscribe to hardware config topic',
      e,
    );
  }
}

/**
 * Returns the current robot model type.
 *
 * If the dynamic hardware config feature flag is enabled and a messenger connector is provided,
 * the model type is determined by the cached hardware config.
 * Otherwise, the model type defaults to RO1/RO2.
 *
 * @returns The current robot model type, if known.
 */
export function getModelType(): ModelTypeEnum | undefined {
  if (!getFeatureFlagSync('disableDynamicHardwareConfig', false)) {
    return convertModelType(cachedHardwareConfig?.config?.hardwareVariant);
  }

  return ModelType.enum.RO1;
}

/**
 * Registers a callback to be invoked whenever a model type update occurs.
 * Returns a function to unregister the listener.
 *
 * @param listener - The callback to invoke when a model type update occurs.
 * @returns A function to unregister the listener.
 */
export function onModelTypeUpdate(listener: ModelUpdateListener): () => void {
  modelUpdateListeners.add(listener);

  return () => {
    modelUpdateListeners.delete(listener);
  };
}

/**
 * Registers a callback to be invoked whenever the arm revision changes.
 * Returns a function to unregister the listener.
 */
export function onRevisionUpdate(listener: RevisionUpdateListener): () => void {
  revisionUpdateListeners.add(listener);

  return () => {
    revisionUpdateListeners.delete(listener);
  };
}

/**
 * Registers a callback invoked when the control-box voltage resolves or
 * changes — including the initial `undefined -> defined` transition during the
 * boot voltage-sampling race. Returns a function to unregister the listener.
 */
export function onCBVoltageUpdate(
  listener: CBVoltageUpdateListener,
): () => void {
  cbVoltageUpdateListeners.add(listener);

  return () => {
    cbVoltageUpdateListeners.delete(listener);
  };
}

/**
 * Returns the cached arm revision from the latest hardware config, if available.
 */
export function getArmRevision(): string | undefined {
  return cachedHardwareConfig?.config?.armRevision;
}

/**
 * Returns the cached arm serial from the latest hardware config, if available.
 *
 * Sourced from joint flash (FlashMetadataKey::ARM_SERIAL_NUMBER) and reported
 * by ACB alongside the revision, so it tracks the arm actually attached rather
 * than a value persisted against the control box.
 */
export function getArmSerialNumber(): string | undefined {
  return cachedHardwareConfig?.config?.armSerialNumber;
}

export function getCBVoltage(): CBVoltage | undefined {
  if (getFeatureFlagSync('disableDynamicHardwareConfig', false)) {
    return undefined;
  }

  const parsed = CBVoltage.safeParse(cachedHardwareConfig?.cbConfig?.voltage);

  return parsed.success ? parsed.data : undefined;
}

// eslint-disable-next-line no-underscore-dangle
export function __setCachedHardwareConfigForTest(
  config: GetHardwareConfigResult | undefined,
): void {
  cachedHardwareConfig = config;
}
